import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Camera, Sparkles, Archive, Pencil, Trash2, Loader2, Image as ImageIcon, History, Lock, Sprout, Stethoscope, ChevronRight } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { COMPANIONS, PLANT_CATALOG, STORAGE_TIPS } from '../lib/data';
import { fileToCompressedDataURL, dataURLToFile } from '../lib/image';
import { useI18n } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import PropagationModal from './PropagationModal';
import ImagePicker from './ImagePicker';
import LivingImage from './living/LivingImage';
import LivingEmoji from './living/LivingEmoji';
import SpeciesHero from './species/SpeciesHero';
import { matchCatalogId } from '../lib/species';
import MakeRecipeButton from './MakeRecipeButton';
import { isEdibleSpecies } from '../lib/shed/sources';
import { showBridgeRewardToast } from '../lib/bridgeReward';
import { guideForPlant, localText } from '../lib/plantGuides';
import './PlantDetailPanel.css';

const normalizedIdentity = (value) => String(value || '').trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
const identitySlug = (value) => normalizedIdentity(value).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64) || 'custom_plant';
const speciesKeyFromName = (value, fallback) => {
    const key = normalizedIdentity(value);
    const catalog = PLANT_CATALOG.find((entry) => [entry.id, entry.name?.da, entry.name?.en, entry.name?.de, entry.name?.sv, entry.name?.no]
        .filter(Boolean).some((name) => normalizedIdentity(name) === key));
    return catalog?.id || identitySlug(value) || fallback;
};

export default function PlantDetailModal({ plant, open, onClose, onChanged }) {
    const { t, locale } = useI18n();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isPremium = !!user?.premium;
    const canAssess = isPremium || !!user?.ai_payg_available;
    const [pendingAssessment, setPendingAssessment] = useState(null);
    const [journal, setJournal] = useState([]);
    const [note, setNote] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [showPropagate, setShowPropagate] = useState(false);
    const [override, setOverride] = useState({});
    const [savingMain, setSavingMain] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [albumBusy, setAlbumBusy] = useState(false);
    const [assess, setAssess] = useState(null);
    // Send to bed
    const [bedPicker, setBedPicker] = useState(false);
    const [beds, setBeds] = useState([]);
    const [bedBusy, setBedBusy] = useState(false);
    const [identityEditing, setIdentityEditing] = useState(false);
    const [identityBusy, setIdentityBusy] = useState(false);
    const panel = useRef(null);
    const [failedPhoto, setFailedPhoto] = useState(null);
    const [identityDraft, setIdentityDraft] = useState({ display_name: '', species_common_name: '', variety: '' });

    useEffect(() => { setPendingAssessment(null); }, [open, plant?.id, user?.user_id]);

    useEffect(() => {
        if (!open || !plant?.id) return;
        setAssess(null);
        setIdentityEditing(false);
        setIdentityDraft({
            display_name: plant.display_name || plant.name || '',
            species_common_name: plant.species_common_name || plant.species || '',
            variety: plant.variety || '',
        });
        let active = true;
        setJournal([]);
        api.get(`/journal/${plant.id}`).then((r) => { if (active) setJournal(r.data || []); })
            .catch(() => { if (active) setJournal([]); });
        return () => { active = false; };
    }, [open, plant?.id, plant?.display_name, plant?.name, plant?.species, plant?.species_common_name, plant?.variety]);

    if (!open || !plant) return null;

    const localPlant = { ...plant, ...(override[plant.id] || {}) };
    const liveHealth = assess?.health ?? localPlant.health;
    const jump = selector => {
        const target = panel.current?.querySelector(selector);
        if (!target) return;
        target.scrollIntoView({ block: 'start', behavior: 'auto' });
        target.focus({ preventScroll: true });
    };

    const saveIdentity = async () => {
        const displayName = identityDraft.display_name.trim();
        const commonName = identityDraft.species_common_name.trim();
        if (!displayName || !commonName) {
            toast.error(locale === 'da' ? 'Skriv både plantenavn og art.' : 'Enter both a plant name and species.');
            return;
        }
        setIdentityBusy(true);
        try {
            const payload = {
                name: displayName,
                display_name: displayName,
                species_common_name: commonName,
                species: speciesKeyFromName(commonName, localPlant.species),
                variety: identityDraft.variety.trim() || null,
            };
            const response = await api.patch(`/plants/${plant.id}`, payload);
            setOverride((current) => ({ ...current, [plant.id]: { ...(current[plant.id] || {}), ...response.data } }));
            setIdentityEditing(false);
            toast.success(locale === 'da' ? 'Plantens navn og sort er opdateret.' : 'Plant identity updated.');
            onChanged?.();
        } catch { toast.error(locale === 'da' ? 'Kunne ikke gemme ændringen.' : 'Could not save the change.'); }
        finally { setIdentityBusy(false); }
    };

    const updateMain = async (image_path) => {
        try {
            await api.patch(`/plants/${plant.id}`, { image_path });
            setOverride((o) => ({ ...o, [plant.id]: { ...(o[plant.id] || {}), image_path } }));
            toast.success(t('plant.main_updated'));
            onChanged?.();
        } catch { toast.error(t('plant.photo_fail')); }
    };

    const onPickMain = async (f) => {
        if (!f) return;
        setSavingMain(true);
        try {
            const fd = new FormData();
            const compact = dataURLToFile(await fileToCompressedDataURL(f), 'plant-main.jpg');
            fd.append('file', compact);
            const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            await updateMain(up.data.path);
        } catch { toast.error(t('plant.upload_fail')); }
        finally { setSavingMain(false); }
    };

    // ---- Album: add a photo straight into the plant's growth album (one tap) ----
    const addAlbumPhoto = async (f) => {
        if (!f) return;
        setAlbumBusy(true);
        setAssess(null);
        try {
            const dataUrl = await fileToCompressedDataURL(f);
            const fd = new FormData();
            fd.append('file', dataURLToFile(dataUrl, 'plant-album.jpg'));
            const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const r = await api.post('/journal', { plant_id: plant.id, note: '', image_path: up.data.path });
            setJournal((p) => [r.data, ...p]);
            await updateMain(up.data.path);
            toast.success(t('plant.photo_saved'));
            onChanged?.();
            setPendingAssessment(dataUrl);
            if (isPremium && !user?.premium_via_plus) {
                try {
                    const a = await api.post('/ai/assess-plant', { plant_id: plant.id, image_base64: dataUrl, locale });
                    setAssess(a.data);
                    onChanged?.();
                    // Physical -> virtual bridge: a best-effort reward the
                    // server may have granted alongside this health check
                    // (see routers/ai.py's `_grant_bridge_reward`). Surfaced
                    // here, not blocking anything above it -- a missing or
                    // null `bridge_reward` (not Premium, no parsed health,
                    // already claimed today) is silently normal.
                    showBridgeRewardToast(a.data?.bridge_reward, t);
                } catch { /* assessment is best-effort */ }
            }
        } catch { toast.error(t('plant.upload_fail')); }
        finally { setAlbumBusy(false); }
    };

    const assessSavedPhoto = async () => {
        if (!pendingAssessment || albumBusy) return;
        setAlbumBusy(true);
        try {
            const { data } = await api.post('/ai/assess-plant', { plant_id: plant.id, image_base64: pendingAssessment, locale });
            setAssess(data);
            setPendingAssessment(null);
            onChanged?.();
            showBridgeRewardToast(data?.bridge_reward, t);
        } catch (err) {
            if (err?.code !== 'ERR_CANCELED') toast.error(locale === 'da' ? 'Se AI-historik under Premium for status.' : 'Check AI history under Premium for the status.');
        } finally { setAlbumBusy(false); }
    };

    // ---- Send this plant to a bed ----
    const openBedPicker = async () => {
        setBedPicker(true);
        try { const r = await api.get('/beds'); setBeds(r.data || []); }
        catch { setBeds([]); }
    };

    const placeInBed = async (bedId) => {
        setBedBusy(true);
        try {
            let id = bedId;
            if (bedId === '__new__') {
                const nb = await api.post('/beds', { name: t('bed.default_name') || 'My bed', rows: 6, cols: 6, cells: [] });
                id = nb.data.id;
            }
            await api.post(`/beds/${id}/place-plant`, { plant_id: plant.id });
            toast.success(t('plant.sent_to_bed'));
            setBedPicker(false);
            navigate('/bed-layout');
        } catch { toast.error(t('plant.bed_fail')); }
        finally { setBedBusy(false); }
    };

    const saveEdit = async (j) => {
        try {
            const r = await api.patch(`/journal/${j.id}`, { note: editText });
            setJournal((list) => list.map((x) => (x.id === j.id ? r.data : x)));
            setEditingId(null); setEditText('');
            toast.success(t('plant.note_updated'));
        } catch { toast.error(t('plant.note_fail')); }
    };

    const removeEntry = async (j) => {
        try {
            await api.delete(`/journal/${j.id}`);
            setJournal((list) => list.filter((x) => x.id !== j.id));
            toast.success(t('plant.note_deleted'));
        } catch { toast.error(t('plant.note_fail')); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!note.trim() && !imageFile) return;
        try {
            let image_path = null;
            if (imageFile) {
                const fd = new FormData();
                const compact = dataURLToFile(await fileToCompressedDataURL(imageFile), 'plant-journal.jpg');
                fd.append('file', compact);
                const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                image_path = up.data.path;
            }
            const r = await api.post('/journal', { plant_id: plant.id, note: note || '', image_path });
            setJournal((p) => [r.data, ...p]);
            if (image_path) await updateMain(image_path);
            setNote(''); setImageFile(null);
            toast.success(t('plant.note_added'));
            onChanged?.();
        } catch {
            toast.error('Error');
        }
    };

    const comp = COMPANIONS[plant.species];
    const cat = PLANT_CATALOG.find((c) => c.id === plant.species);
    const useGuide = guideForPlant(plant, cat?.cat);
    const albumPhotos = journal.filter((j) => j.image_path)
        .map((j) => ({ id: j.id, src: j.image_path, date: j.created_at }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    return (
        <div data-testid="plant-detail-modal" className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-brand-ink/40 p-0 sm:p-4 backdrop-blur-sm">
            <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="plant-detail-title" className="plant-detail-panel bg-brand-bg w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border border-brand-border max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-brand-bg flex items-center justify-between px-5 py-4 border-b border-brand-border z-10">
                    <h3 id="plant-detail-title" className="font-serif text-xl text-brand-ink">{localPlant.emoji} {localPlant.display_name || localPlant.name}</h3>
                    <button aria-label={locale === 'da' ? 'Luk plantedetaljer' : 'Close plant details'} data-testid="btn-close-plant-detail" onClick={onClose} className="p-2 hover:bg-brand-bg2 rounded-full">
                        <X size={18} strokeWidth={1.6} />
                    </button>
                </div>
                <nav className="plant-detail-shortcuts" aria-label={locale === 'da' ? 'Planteafsnit' : 'Plant sections'}>
                    <button onClick={() => jump('[data-testid="plant-identity"]')}>{locale === 'da' ? 'Planten' : 'Plant'}</button>
                    <button onClick={() => jump('[data-testid="plant-album"]')}>{t('plant.album')}</button>
                    <button onClick={() => jump('[data-testid="input-journal-note"]')}>{t('garden.journal')}</button>
                </nav>

                <div className="p-5 space-y-5">
                    <div className="relative">
                        {localPlant.image_path ? (
                            <LivingImage src={fileUrl(localPlant.image_path)} alt={plant.name} className="w-full h-44 rounded-2xl border border-brand-border" data-testid="plant-main-photo" />
                        ) : (
                            matchCatalogId(plant, PLANT_CATALOG)
                                ? <SpeciesHero kind="plant" id={matchCatalogId(plant, PLANT_CATALOG)} emoji={plant.emoji} name={plant.name} className="w-full h-44 rounded-2xl border border-brand-border" />
                                : <div className="w-full h-44 rounded-2xl bg-gradient-to-b from-brand-sage/25 to-brand-bg2 flex items-end justify-center pb-6 text-6xl"><LivingEmoji seed={plant.id}>{plant.emoji || '🌱'}</LivingEmoji></div>
                        )}
                        <ImagePicker testId="main-photo-pick" onSelect={onPickMain}>
                            {(open2) => (
                                <button type="button" onClick={open2} disabled={savingMain} data-testid="btn-change-main-photo" className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 bg-brand-ink/70 text-white text-xs px-3 py-1.5 rounded-full hover:bg-brand-ink disabled:opacity-50">
                                    {savingMain ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />} {t('plant.change_photo')}
                                </button>
                            )}
                        </ImagePicker>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <Stat label={t('garden.stage')} value={localPlant.stage} />
                        <Stat label={t('plant.days_growing')} value={localPlant.days} />
                        <Stat label={t('garden.health')} value={Number.isFinite(liveHealth) ? `${liveHealth}%` : '—'} />
                    </div>

                    <div tabIndex={-1} className="bg-brand-card border border-brand-border rounded-2xl p-4" data-testid="plant-identity">
                        <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{locale === 'da' ? 'Planteidentitet' : 'Plant identity'}</div>
                            {!identityEditing && <button type="button" data-testid="edit-plant-identity" onClick={() => setIdentityEditing(true)} className="text-xs text-brand-green inline-flex items-center gap-1"><Pencil size={12} /> {t('common.edit')}</button>}
                        </div>
                        {identityEditing ? (
                            <div className="space-y-2">
                                <input value={identityDraft.display_name} onChange={(e) => setIdentityDraft((draft) => ({ ...draft, display_name: e.target.value }))} placeholder={locale === 'da' ? 'Mit plantenavn' : 'My plant name'} className="w-full bg-brand-bg border border-brand-border rounded-xl px-3 py-2 text-sm" />
                                <input value={identityDraft.species_common_name} onChange={(e) => setIdentityDraft((draft) => ({ ...draft, species_common_name: e.target.value }))} placeholder={locale === 'da' ? 'Art, fx tomat' : 'Species, e.g. tomato'} className="w-full bg-brand-bg border border-brand-border rounded-xl px-3 py-2 text-sm" />
                                <input value={identityDraft.variety} onChange={(e) => setIdentityDraft((draft) => ({ ...draft, variety: e.target.value }))} placeholder={locale === 'da' ? 'Sort/cultivar (valgfri)' : 'Variety/cultivar (optional)'} className="w-full bg-brand-bg border border-brand-border rounded-xl px-3 py-2 text-sm" />
                                <div className="flex gap-2"><button type="button" data-testid="save-plant-identity" disabled={identityBusy} onClick={saveIdentity} className="bg-brand-green text-white px-3 py-2 rounded-full text-xs disabled:opacity-50">{identityBusy ? 'Saving…' : t('common.save')}</button><button type="button" onClick={() => setIdentityEditing(false)} className="bg-brand-bg2 px-3 py-2 rounded-full text-xs">{t('common.cancel')}</button></div>
                            </div>
                        ) : (
                            <div className="text-sm text-brand-ink2 space-y-1"><p><span className="text-brand-ink3">{locale === 'da' ? 'Art' : 'Species'}:</span> {localPlant.species_common_name || localPlant.species || '—'}</p>{localPlant.variety && <p><span className="text-brand-ink3">{locale === 'da' ? 'Sort' : 'Variety'}:</span> {localPlant.variety}</p>}{localPlant.scientific_name && <p className="italic text-xs text-brand-ink3">{localPlant.scientific_name}</p>}</div>
                        )}
                    </div>

                    {/* Send to a bed */}
                    <button
                        data-testid="btn-send-to-bed"
                        onClick={openBedPicker}
                        className="w-full inline-flex items-center justify-between gap-2 bg-brand-card border border-brand-border rounded-2xl px-4 py-3 hover:border-brand-green transition-colors"
                    >
                        <span className="inline-flex items-center gap-2 text-sm text-brand-ink">
                            <Sprout size={16} className="text-brand-green" /> {t('plant.send_to_bed')}
                        </span>
                        <ChevronRight size={16} className="text-brand-ink3" />
                    </button>

                    {/* Growth album */}
                    <div tabIndex={-1} className="bg-brand-card border border-brand-border rounded-2xl p-4" data-testid="plant-album">
                        <div className="flex items-center gap-2 mb-3">
                            <History size={14} className="text-brand-green" strokeWidth={1.8} />
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('plant.album')}</div>
                            <span className="text-[10px] text-brand-ink3 ml-auto">{albumPhotos.length} {t('plant.photos')}</span>
                        </div>

                        <ImagePicker testId="album-pick" onSelect={addAlbumPhoto}>
                            {(open2) => (
                                <button type="button" data-testid="btn-add-album-photo" onClick={open2} disabled={albumBusy}
                                    className="w-full mb-3 inline-flex items-center justify-center gap-2 bg-brand-green text-white text-sm py-2.5 rounded-full hover:bg-brand-greenDark disabled:opacity-50">
                                    {albumBusy ? <><Loader2 size={14} className="animate-spin" /> {t('plant.saving')}</> : <><Camera size={14} /> {t('plant.add_photo')}</>}
                                </button>
                            )}
                        </ImagePicker>

                        {albumPhotos.length === 0 ? (
                            <p className="text-xs text-brand-ink3 italic text-center py-3">{t('plant.album_empty')}</p>
                        ) : (
                            <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1" data-testid="album-track">
                                {albumPhotos.map((p, i) => (
                                    <div key={p.id} className="flex-shrink-0 w-24">
                                        <img src={fileUrl(p.src)} alt="" className="w-24 h-24 object-cover rounded-2xl border border-brand-border" loading="lazy" />
                                        <p className="text-[10px] text-brand-ink3 mt-1 text-center">{new Date(p.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                                        <p className="text-[9px] uppercase tracking-widest font-semibold text-brand-green text-center">{i === 0 ? t('plant.before') : (i === albumPhotos.length - 1 ? t('plant.now') : `#${i + 1}`)}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {pendingAssessment && canAssess && (!isPremium || user?.premium_via_plus) && (
                            <button disabled={albumBusy} onClick={assessSavedPhoto} className="mt-3 w-full rounded-full border border-brand-ochre/40 p-3 disabled:opacity-50">
                                {locale === 'da' ? 'Sundhedstjek med AI — vis pris først' : 'AI health check — show price first'}
                            </button>
                        )}
                        {!canAssess && (
                            <button data-testid="album-upsell" onClick={() => navigate('/premium')} className="mt-3 w-full text-left bg-gradient-to-br from-brand-ochre to-brand-terracotta text-white rounded-2xl p-3">
                                <div className="text-sm font-serif">{t('plant.assess_upsell_title')}</div>
                                <div className="text-xs text-white/80 mt-0.5">{t('plant.assess_upsell_sub')}</div>
                            </button>
                        )}
                    </div>

                    {/* AI health assessment result (Premium) */}
                    {assess && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4" data-testid="assess-result">
                            <div className="flex items-center gap-2 mb-2">
                                <Stethoscope size={15} className="text-sky-500" />
                                <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('plant.health_check')}</div>
                                {typeof assess.health === 'number' && (
                                    <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${assess.health > 70 ? 'bg-brand-green/15 text-brand-green' : assess.health > 50 ? 'bg-amber-500/15 text-amber-600' : 'bg-brand-terracotta/15 text-brand-terracotta'}`}>{assess.health}%</span>
                                )}
                            </div>
                            {assess.summary && <p className="text-sm text-brand-ink2 leading-relaxed">{assess.summary}</p>}
                            {assess.needs_doctor && Array.isArray(assess.advice) && assess.advice.length > 0 && (
                                <div className="mt-3 bg-brand-bg2 rounded-xl p-3">
                                    <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-terracotta mb-1.5 inline-flex items-center gap-1"><Sparkles size={11} /> {t('plant.doctor_advice')}</div>
                                    <ul className="space-y-1.5">
                                        {assess.advice.slice(0, 4).map((a, i) => (
                                            <li key={`${i}-${a}`} className="text-sm text-brand-ink2 flex gap-2"><span className="text-brand-green">•</span> {a}</li>
                                        ))}
                                    </ul>
                                    <button
                                        type="button"
                                        onClick={() => { onClose?.(); navigate('/garden-doctor', { state: { plantId: plant.id, plantName: plant.name } }); }}
                                        className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-brand-terracotta text-white text-sm py-2.5 rounded-full hover:opacity-90"
                                    >
                                        <Stethoscope size={14} /> {locale === 'da' ? `Spørg Havedoktoren om ${plant.name}` : `Ask Garden Doctor about ${plant.name}`}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {plant.id && isEdibleSpecies(plant.species) && <MakeRecipeButton source={`garden:${plant.id}`} />}
                    {(localPlant.care || localPlant.sun || localPlant.water) && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4" data-testid="ai-care-info">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles size={14} className="text-brand-green" />
                                <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('plant.identified_info')}</div>
                                {typeof localPlant.ai_confidence === 'number' && <span className="text-[10px] text-brand-ink3">· {localPlant.ai_confidence}%</span>}
                            </div>
                            {plant.species && <p className="text-xs italic text-brand-ink3 mb-1">{plant.species}</p>}
                            {localPlant.care && <p className="text-sm text-brand-ink2 leading-relaxed">{localPlant.care}</p>}
                            <div className="mt-2 flex flex-wrap gap-3 text-xs text-brand-ink3">
                                {localPlant.sun && <span>☀️ {localPlant.sun}</span>}
                                {localPlant.water && <span>💧 {localPlant.water}</span>}
                            </div>
                        </div>
                    )}

                    {cat && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-2">{t('plant.care_tips')}</div>
                            <p className="text-sm text-brand-ink2 leading-relaxed">{cat.info[locale] || cat.info.en || cat.info.da}</p>
                            <div className="mt-3 flex gap-4 text-xs text-brand-ink3">
                                {(() => {
                                    const tr = (cat2, val) => { const r = t(`attr.${cat2}.${val}`); return r === `attr.${cat2}.${val}` ? val : r; };
                                    return <>
                                        <span>☀️ {tr('sun', cat.sun)}</span>
                                        <span>💧 {tr('water', cat.water)}</span>
                                        <span>🌱 {(cat.soil || '').split(',').map(s => tr('soil', s.trim())).join(', ')}</span>
                                    </>;
                                })()}
                            </div>
                        </div>
                    )}

                    {plant.species && STORAGE_TIPS[plant.species] && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Archive size={14} className="text-brand-ochre" strokeWidth={1.8} />
                                <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('plant.harvest_storage')}</div>
                            </div>
                            <div className="space-y-2 mt-1">
                                {Object.entries(STORAGE_TIPS[plant.species]).map(([method, txt]) => {
                                    const label = { fridge: '❄️ Fridge', freezer: '🧊 Freezer', basement: '🏚️ Cellar', drying: '🌾 Drying', jam: '🍯 Jam', pickle: '🥒 Pickling', oil: '🫒 In oil', best: '✨ Best' }[method] || method;
                                    return (
                                        <div key={method} className="text-xs text-brand-ink2">
                                            <strong className="text-brand-ink">{label}:</strong> <span>{txt[locale] || txt.en || txt.da}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {useGuide && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4" data-testid="plant-use-guide">
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3">{locale === 'da' ? 'Hvad kan jeg bruge den til?' : 'What can I use it for?'}</div>
                            <div className="space-y-3">
                                {(useGuide.uses || []).map(([icon, title, body]) => (
                                    <div key={localText(title, locale)} className="flex gap-3"><span className="text-xl">{icon}</span><div><div className="text-sm font-medium text-brand-ink">{localText(title, locale)}</div><p className="text-xs text-brand-ink2 leading-relaxed mt-0.5">{localText(body, locale)}</p></div></div>
                                ))}
                            </div>
                            {useGuide.ripening && <div className="mt-4 bg-brand-green/10 rounded-2xl p-3"><div className="text-[10px] uppercase tracking-widest font-semibold text-brand-green mb-1">{locale === 'da' ? 'Modning & høst' : 'Ripening & harvest'}</div><p className="text-xs text-brand-ink2 leading-relaxed">{localText(useGuide.ripening, locale)}</p></div>}
                            {useGuide.warning && <div className="mt-3 bg-brand-terracotta/10 border border-brand-terracotta/25 rounded-2xl p-3 text-xs text-brand-terracotta leading-relaxed">⚠️ {localText(useGuide.warning, locale)}</div>}
                            <p className="text-[10px] text-brand-ink3 mt-3">{locale === 'da' ? 'Brug kun korrekt identificerede, usprøjtede planter. Råd om indtagelse og hudbrug er generelle og erstatter ikke faglig rådgivning.' : 'Use only correctly identified, unsprayed plants. Food and skin-use guidance is general and does not replace professional advice.'}</p>
                        </div>
                    )}

                    <button
                        data-testid="btn-propagate"
                        onClick={() => setShowPropagate(true)}
                        className="w-full bg-brand-green hover:bg-brand-greenDark text-white p-4 rounded-2xl flex items-center justify-between"
                    >
                        <div className="text-left">
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-bg/70 inline-flex items-center gap-1"><Sparkles size={11} /> AI</div>
                            <div className="font-serif text-base mt-0.5">{t('plant.propagate')}</div>
                        </div>
                        <span className="text-2xl">✂️</span>
                    </button>

                    {comp && (comp.good.length > 0 || comp.bad.length > 0) && (
                        <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-2">{t('plant.good_companions')}</div>
                            {comp.good.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {comp.good.map((id) => {
                                        const c = PLANT_CATALOG.find((p) => p.id === id);
                                        return c && <span key={id} className="text-xs bg-brand-sage/20 text-brand-green px-3 py-1 rounded-full">{c.emoji} {c.name[locale] || c.name.en || c.name.da}</span>;
                                    })}
                                </div>
                            )}
                            {comp.bad.length > 0 && (
                                <>
                                    <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-2 mt-3">{t('plant.avoid_with')}</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {comp.bad.map((id) => {
                                            const c = PLANT_CATALOG.find((p) => p.id === id);
                                            return c && <span key={id} className="text-xs bg-brand-terracotta/15 text-brand-terracotta px-3 py-1 rounded-full">{c.emoji} {c.name[locale] || c.name.en || c.name.da}</span>;
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                        <h4 className="font-serif text-lg text-brand-ink mb-3">{t('garden.journal')}</h4>
                        <form onSubmit={submit} className="space-y-3 mb-4">
                            <textarea
                                data-testid="input-journal-note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder={t('garden.how_today')}
                                rows="2"
                                className="w-full bg-brand-bg border border-brand-border rounded-2xl px-4 py-3 text-sm resize-none"
                            />
                            <div className="flex items-center gap-2">
                                <ImagePicker testId="journal-pick" onSelect={(f) => setImageFile(f || null)}>
                                    {(open2) => (
                                        <button type="button" onClick={open2} className="inline-flex items-center gap-1.5 bg-brand-bg2 hover:bg-brand-border px-3 py-2 rounded-full cursor-pointer text-xs">
                                            <Camera size={13} strokeWidth={1.8} />
                                            <span>{imageFile?.name?.slice(0, 14) || t('plant.photo')}</span>
                                        </button>
                                    )}
                                </ImagePicker>
                                <button data-testid="btn-add-journal" type="submit" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-green text-white text-xs px-3 py-2 rounded-full hover:bg-brand-greenDark">
                                    <Plus size={13} strokeWidth={2} /> {t('plant.add_note')}
                                </button>
                            </div>
                        </form>

                        <div className="space-y-3">
                            {journal.length === 0 && <p className="text-sm text-brand-ink3 italic">{t('plant.no_notes')}</p>}
                            {journal.map((j) => (
                                <div key={j.id} className="border-l-2 border-brand-sage pl-3" data-testid={`journal-entry-${j.id}`}>
                                    {j.image_path && <img src={fileUrl(j.image_path)} alt="" className="rounded-xl mb-2 max-w-[200px] border border-brand-border" />}
                                    {editingId === j.id ? (
                                        <div className="space-y-2">
                                            <textarea
                                                data-testid="edit-journal-input"
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                rows="2"
                                                className="w-full bg-brand-bg border border-brand-border rounded-xl px-3 py-2 text-sm resize-none"
                                            />
                                            <div className="flex gap-2">
                                                <button data-testid={`save-journal-${j.id}`} onClick={() => saveEdit(j)} className="bg-brand-green text-white text-xs px-3 py-1.5 rounded-full">{t('common.save')}</button>
                                                <button onClick={() => { setEditingId(null); setEditText(''); }} className="bg-brand-bg2 text-brand-ink text-xs px-3 py-1.5 rounded-full">{t('common.cancel')}</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {j.note && <p className="text-sm text-brand-ink2">{j.note}</p>}
                                            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
                                                <p className="text-[10px] text-brand-ink3">{new Date(j.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                                                <button data-testid={`edit-journal-${j.id}`} onClick={() => { setEditingId(j.id); setEditText(j.note || ''); }} className="text-[11px] text-brand-ink3 hover:text-brand-green inline-flex items-center gap-1"><Pencil size={11} /> {t('common.edit')}</button>
                                                <button data-testid={`delete-journal-${j.id}`} onClick={() => removeEntry(j)} className="text-[11px] text-brand-ink3 hover:text-brand-terracotta inline-flex items-center gap-1"><Trash2 size={11} /> {t('common.delete')}</button>
                                                {j.image_path && <button data-testid={`set-main-${j.id}`} onClick={() => updateMain(j.image_path)} className="text-[11px] text-brand-ink3 hover:text-brand-green inline-flex items-center gap-1"><ImageIcon size={11} /> {t('plant.set_main')}</button>}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {bedPicker && (
                <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-brand-ink/45 backdrop-blur-sm" onClick={() => setBedPicker(false)} data-testid="bed-picker">
                    <div className="bg-brand-bg w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl border border-brand-border p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-3">
                            <Sprout size={18} className="text-brand-green" />
                            <h3 className="font-serif text-lg text-brand-ink">{t('plant.send_to_bed')}</h3>
                        </div>
                        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                            {beds.map((b) => (
                                <button key={b.id} data-testid={`pick-bed-${b.id}`} disabled={bedBusy} onClick={() => placeInBed(b.id)}
                                    className="w-full text-left bg-brand-card border border-brand-border rounded-2xl px-4 py-3 hover:border-brand-green transition-colors disabled:opacity-50 inline-flex items-center justify-between">
                                    <span className="text-sm text-brand-ink truncate">{b.name}</span>
                                    <ChevronRight size={15} className="text-brand-ink3 flex-shrink-0" />
                                </button>
                            ))}
                            <button data-testid="pick-bed-new" disabled={bedBusy} onClick={() => placeInBed('__new__')}
                                className="w-full text-left bg-brand-green/10 border border-brand-green/30 text-brand-green rounded-2xl px-4 py-3 hover:bg-brand-green/20 disabled:opacity-50 inline-flex items-center gap-2">
                                {bedBusy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {t('plant.new_bed')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <PropagationModal open={showPropagate} onClose={() => setShowPropagate(false)} plant={plant} />
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-brand-card border border-brand-border rounded-2xl p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-1">{label}</div>
            <div className="font-serif text-xl text-brand-ink">{value}</div>
        </div>
    );
}
